import * as React from 'react';

import {
  Badge,
  Button,
  ConfirmPopoverButton,
  DialogClosingConfirmation,
  EntityDetails,
  LocalizedDate,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft, Trash2 } from 'lucide-react';

import { categoryDeleteAtom } from '../../../_graphql/mutationCategoryDelete';
import { categoryUpdateAtom } from '../../../_graphql/mutationCategoryUpdate';
import * as state from '../../../_state/categoryDetail.state';
import {
  $categoryDiscardBaseline,
  $hasCategoryUnsavedChanges,
  $resetCategoryFormToBaseline,
  CategoryFormState,
} from '../../../_state/categoryForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { usePopulateCategoryForm } from '../../../hooks/usePopulateCategoryForm';
import { useTranslations } from '../../../i18n/useTranslations';

import { CategoryTabDetails } from './CategoryTabDetails';

type CategoryDetailTab = 'details';

export const CategoryDetailView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const [currentTab, setCurrentTab] =
    React.useState<CategoryDetailTab>('details');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<CategoryDetailTab | null>(null);

  const categoryId = useAtomValue(state.$categoryId);
  const {
    data: category,
    isFetching,
    refetch,
  } = useAtomValue(state.$remoteCategoryDetail);
  const isLoading = useAtomValue(state.$isCategoryLoading);
  const error = useAtomValue(state.$categoryError);
  const hasUnsavedChanges = useAtomValue($hasCategoryUnsavedChanges);

  const [, resetForm] = useAtom($resetCategoryFormToBaseline);
  const [discardBaseline, setDiscardBaseline] = useAtom(
    $categoryDiscardBaseline,
  );

  const deleteCategory = useSetAtom(categoryDeleteAtom({}));
  const updateCategory = useSetAtom(categoryUpdateAtom({}));

  const toast = useToast();
  usePopulateCategoryForm({ category });

  const handleBack = React.useCallback(() => {
    setLocation('/inventory');
  }, [setLocation]);

  const handleDiscard = React.useCallback(() => {
    resetForm();
    toast.success(t.inventoryView.categoryDetailView.toast.discarded);
  }, [resetForm, toast, t]);

  const handleSaveUpdates = React.useCallback(
    async (data?: CategoryFormState) => {
      if (!categoryId || !data) return;

      if (!data.name.trim()) {
        toast.error(
          t.inventoryView.categoryCreateDialog.validation.nameRequired,
        );
        return;
      }

      const invalidAttributes = data.attributes.filter(
        (attr) => !attr.name.trim(),
      );
      if (invalidAttributes.length > 0) {
        toast.error(
          t.inventoryView.categoryCreateDialog.validation.attributeNameRequired,
        );
        return;
      }

      try {
        setIsUpdating(true);
        const attributesRecord = data.attributes.reduce(
          (acc, attr) => {
            acc[attr.name.trim()] = {
              name: attr.name.trim(),
              description: attr.description?.trim() ?? '',
              dataType: attr.dataType,
              required: attr.required,
              validationExpr: attr.validationExpr,
              options: attr.options,
            };
            return acc;
          },
          {} as Record<
            string,
            {
              name: string;
              description: string;
              dataType: string;
              required: boolean;
              validationExpr: string | null;
              options: string[] | null;
            }
          >,
        );
        await updateCategory({
          id: categoryId,
          name: data.name.trim(),
          description: data.description.trim() || null,
          attributes: attributesRecord,
        });

        setDiscardBaseline(data);
        toast.success(t.inventoryView.categoryDetailView.toast.updated);
        refetch?.();
      } catch (err) {
        toast.error(
          t.inventoryView.categoryDetailView.toast.updateError(
            err instanceof Error ? err.message : 'Unknown error',
          ),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [categoryId, updateCategory, setDiscardBaseline, toast, t, refetch],
  );

  const handleBeforeChangeTab = React.useCallback(
    (newTab: string) => {
      if (hasUnsavedChanges) {
        setPendingTabChange(newTab as CategoryDetailTab);
        return false;
      }
      return true;
    },
    [hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm();
      setCurrentTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (!categoryId) return;

    try {
      setIsDeleting(true);
      await deleteCategory({ id: categoryId });
      toast.success(t.inventoryView.categoryDetailView.messages.deleteSuccess);
      setLocation('/inventory');
    } catch (err) {
      toast.error(
        t.inventoryView.categoryDetailView.messages.deleteError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  }, [categoryId, deleteCategory, toast, setLocation, t]);

  const tools = React.useMemo(() => {
    if (!category) return null;
    return (
      <ConfirmPopoverButton
        onConfirm={handleDelete}
        message={t.inventoryView.categoryDetailView.messages.deleteConfirmation}
        confirmLabel={t.inventoryView.common.confirmDelete}
        cancelLabel={t.inventoryView.common.cancel}
        buttonProps={{
          variant: 'outline',
          color: 'failure',
          icon: <Trash2 className="size-4" />,
          isDisabled: isDeleting,
        }}
      >
        {isDeleting
          ? t.inventoryView.categoryDetailView.actions.deleting
          : t.inventoryView.categoryDetailView.actions.delete}
      </ConfirmPopoverButton>
    );
  }, [category, handleDelete, isDeleting, t]);

  if (isLoading || isFetching) {
    return (
      <div className="@CategoryDetailView flex h-full flex-col">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-1/4 rounded bg-neutral/20"></div>
            <div className="h-8 w-1/3 rounded bg-neutral/20"></div>
            <div className="h-32 rounded bg-neutral/20"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="@CategoryDetailView flex h-full flex-col p-6">
        <div className="text-failure">
          Error:{' '}
          {error?.message ??
            t.inventoryView.categoryDetailView.messages.categoryNotFound}
        </div>
        <Button variant="outline" onPress={handleBack} className="mt-4 w-fit">
          <ArrowLeft className="mr-2 size-4" />
          {t.inventoryView.categoryDetailView.actions.backToCategories}
        </Button>
      </div>
    );
  }

  const attributeCount = category.attributes.length;

  return (
    <div className="@CategoryDetailView flex h-full flex-col">
      <EntityDetails
        className="relative"
        onChangeTab={(tab) => setCurrentTab(tab as CategoryDetailTab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={currentTab}
        onBackPressed={handleBack}
        primaryTitle={category.name}
        subtitle={
          <>
            {t.inventoryView.categoryDetailView.fields.created}{' '}
            <LocalizedDate date={category.timestamps.createdAt} />
          </>
        }
        badges={
          <Badge color="neutral" size="small">
            {attributeCount}{' '}
            {attributeCount === 1
              ? t.inventoryView.categoriesPanel.badges.attribute
              : t.inventoryView.categoriesPanel.badges.attributes}
          </Badge>
        }
        headerActions={tools}
        tabs={{
          details: {
            title: t.inventoryView.categoryDetailView.tabs.details,
            content: () => (
              <CategoryTabDetails
                category={category}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
        }}
      />
      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={t.inventoryView.categoryDetailView.messages.unsavedDataMessage}
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </div>
  );
};
